import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, Output, ViewEncapsulation } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { Option, SelectionSortDirection } from 'common/components/select/select.component';

const DEFAULT_EMPTY_VALUE = {
    name: '',
    value: ''
};

@Component({
    selector: 'utc-select-checkbox',
    templateUrl: './select-checkbox.component.html',
    styleUrls: ['./select-checkbox.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class SelectCheckboxComponent implements AfterViewInit {
    @Input() selectId: string;
    @Input() control: string;
    @Input() group: UntypedFormGroup;
    @Input() defaultOption?: string;
    @Input() multiple: boolean = true;
    @Input() variant: 'primary' | 'default' = 'default';
    @Input() size: 'default' | 'small' = 'default';
    @Input() shadow: 'light' | 'default' | 'none' = 'default';
    @Input() sort: SelectionSortDirection;
    @Input() required: boolean;
    @Input() placeholder: string;
    @Input() error: boolean;
    @Input() errorMessage: string | null;
    @Input() infoMessage: string;
    @Input() disabled: boolean | false;
    @Input() showSelectAll: boolean = false;
    @Input() selectAllLabel: string = 'Select All';
    @Output() onChange: EventEmitter<Option[]> = new EventEmitter();

    isOpen: boolean;
    options$ = new BehaviorSubject<Option[]>([]);

    public minimizeLabel: boolean;
    public valueText: string;
    public defaultEmptyVal: { name: string, value: string } = DEFAULT_EMPTY_VALUE;

    constructor(
        private cdr: ChangeDetectorRef
    ) {
    }

    get options() {
        return this.options$.getValue();
    }

    @Input()
    set options(value: Option[]) {
        if (this.sort) {
            let sortedOptions = value?.slice().sort((valueA, valueB) => valueA.name.toLowerCase().localeCompare(valueB.name.toLowerCase()));
            if (this.sort === SelectionSortDirection.DESCENDING) {
                sortedOptions = sortedOptions?.slice().reverse();
            }
            this.options$.next(sortedOptions);

            return;
        }

        this.options$.next(value);
    }

    ngAfterViewInit() {
        this.defaultEmptyVal = {
            name: this.placeholder,
            value: ''
        };
        this.minimizeLabel = this.group.controls[this.control].value !== '';
        this.valueText = this.setValueText(this.group.controls[this.control].value, this.multiple);

        this.cdr.detectChanges();

        this.group.controls[this.control].valueChanges.subscribe((change: string) => {
            this.minimizeLabel = change !== '';

            this.valueText = this.setValueText(change, this.multiple);
            this.cdr.detectChanges();
        });
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setValueText(value: any, multiple: boolean) {
        const controlValue = value;
        this.onChange.emit(controlValue);

        if (multiple) {
            if (
                (this.group.controls[this.control].value.length === 1 &&
                this.group.controls[this.control].value[0].value === '') || (this.group.controls[this.control].value === '')
            ) {
                this.minimizeLabel = false;

                return '';
            }

            return Object.keys(controlValue).length ?
                controlValue.filter((item: Option) => item.value && item.value !== '').map((item: Option) => item.name).filter(Boolean).join(',') :
                '';
        }

        if (controlValue && controlValue[0]?.value === '') {
            this.minimizeLabel = false;

            return '';
        }

        return controlValue && controlValue[0] ? controlValue[0].name || controlValue : '';
    }

    handleClick(close?: boolean) {
        if (this.disabled) {
            return;
        }

        if (close) {
            this.isOpen = false;
        }
        else {
            this.isOpen = !this.isOpen;
        }

        this.cdr.detectChanges();
    }

    isOptionSelected(option: Option): boolean {
        const selectedValue = this.group?.controls?.[this.control]?.value;

        if (!Array.isArray(selectedValue)) {
            return false;
        }

        return selectedValue.some((item: Option) => item?.value === option.value);
    }

    isSelectAllSelected(): boolean {
        const selectableOptions = this.getSelectableOptions();

        if (!selectableOptions.length) {
            return false;
        }

        return selectableOptions.every((option) => this.isOptionSelected(option));
    }

    onSelectAllChange(event: Event) {
        const input = event.target as HTMLInputElement;

        if (!input || input.type !== 'checkbox') {
            return;
        }

        event.stopPropagation();

        const selectableOptions = this.getSelectableOptions();
        this.group.controls[this.control].setValue(input.checked && selectableOptions.length ? selectableOptions : '');
    }

    onCheckboxChange(event: Event, option: Option) {
        const input = event.target as HTMLInputElement;

        if (!input || input.type !== 'checkbox') {
            return;
        }

        event.stopPropagation();

        const currentValue = this.group.controls[this.control].value;
        const selectedOptions: Option[] = Array.isArray(currentValue)
            ? currentValue.filter((item: Option) => item?.value)
            : [];

        const nextValue = input.checked
            ? (selectedOptions.some((item) => item.value === option.value) ? selectedOptions : [...selectedOptions, option])
            : selectedOptions.filter((item) => item.value !== option.value);

        this.group.controls[this.control].setValue(nextValue.length ? nextValue : '');
    }

    private getSelectableOptions(): Option[] {
        return (this.options$.getValue() || []).filter((option) => option?.value && !option.disabled);
    }
}
